import React, { useRef, useEffect } from 'react';

/**
 * Ketone Molecules Animation - PREMIUM WOW Effect
 * - Floating ketone/DNA molecules
 * - Energy pulses
 * - Matrix-style data streams
 * - Breathing ecosystem effect
 */
const KetoneMolecules = React.memo(() => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;
    
    // Particle collections
    let molecules = [];
    let energyPulses = [];
    let dataStreams = [];
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };
    
    const initParticles = () => {
      molecules = [];
      dataStreams = [];
      
      // Create floating molecules
      for (let i = 0; i < 25; i++) {
        molecules.push(createMolecule());
      }
      
      // Create data streams (Matrix effect)
      const columns = Math.floor(canvas.width / 50);
      for (let i = 0; i < columns; i++) {
        if (Math.random() > 0.7) {
          dataStreams.push({
            x: i * 50 + Math.random() * 25,
            y: Math.random() * canvas.height,
            speed: Math.random() * 1.5 + 0.5,
            chars: [],
            length: Math.floor(Math.random() * 8) + 4,
            opacity: Math.random() * 0.3 + 0.1,
          });
          const stream = dataStreams[dataStreams.length - 1];
          const chars = 'KETO01βγδ∆ATP'.split('');
          for (let j = 0; j < stream.length; j++) {
            stream.chars.push(chars[Math.floor(Math.random() * chars.length)]);
          }
        }
      }
    };
    
    const createMolecule = () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 30 + 15,
      type: Math.random() > 0.5 ? 'ketone' : 'dna',
      speedX: (Math.random() - 0.5) * 0.3,
      speedY: (Math.random() - 0.5) * 0.3,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.01,
      opacity: Math.random() * 0.4 + 0.2,
      phase: Math.random() * Math.PI * 2,
    });
    
    const createEnergyPulse = () => ({
      x: canvas.width / 2,
      y: canvas.height / 2,
      radius: 0,
      maxRadius: Math.max(canvas.width, canvas.height) * 0.6,
      speed: 2,
      opacity: 0.3,
    });
    
    resize();
    window.addEventListener('resize', resize);
    
    // Draw ketone molecule (β-hydroxybutyrate structure)
    const drawKetone = (mol) => {
      ctx.save();
      ctx.translate(mol.x, mol.y);
      ctx.rotate(mol.rotation);
      ctx.globalAlpha = mol.opacity;
      
      const s = mol.size * 0.4;
      
      // Carbon chain with oxygen
      ctx.strokeStyle = '#a3e635';
      ctx.lineWidth = 2;
      ctx.shadowBlur = 10;
      ctx.shadowColor = '#a3e635';
      
      // Draw bonds
      ctx.beginPath();
      ctx.moveTo(-s * 2, 0);
      ctx.lineTo(-s, 0);
      ctx.lineTo(0, -s * 0.8);
      ctx.lineTo(s, 0);
      ctx.lineTo(s * 2, 0);
      ctx.stroke();
      
      // Draw atoms
      const atoms = [
        { x: -s * 2, y: 0, color: '#a3e635' },
        { x: -s, y: 0, color: '#a3e635' },
        { x: 0, y: -s * 0.8, color: '#ff6b6b' }, // Oxygen - red
        { x: s, y: 0, color: '#a3e635' },
        { x: s * 2, y: 0, color: '#a3e635' },
      ];
      
      atoms.forEach(atom => {
        ctx.beginPath();
        ctx.arc(atom.x, atom.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = atom.color;
        ctx.shadowColor = atom.color;
        ctx.fill();
      });
      
      ctx.restore();
    };
    
    // Draw DNA helix
    const drawDNA = (mol) => {
      ctx.save();
      ctx.translate(mol.x, mol.y);
      ctx.rotate(mol.rotation);
      ctx.globalAlpha = mol.opacity;
      
      const length = mol.size * 2;
      const amplitude = mol.size * 0.4;
      
      ctx.strokeStyle = '#a3e635';
      ctx.lineWidth = 2;
      ctx.shadowBlur = 8;
      ctx.shadowColor = '#a3e635';
      
      // Draw two helixes
      for (let strand = 0; strand < 2; strand++) {
        ctx.beginPath();
        for (let i = 0; i <= 20; i++) {
          const t = i / 20;
          const y = -length / 2 + t * length;
          const x = Math.sin(t * Math.PI * 3 + mol.phase + strand * Math.PI) * amplitude;
          
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      
      // Draw base pairs
      ctx.strokeStyle = '#a3e635';
      ctx.lineWidth = 1;
      ctx.globalAlpha = mol.opacity * 0.5;
      
      for (let i = 1; i < 20; i += 3) {
        const t = i / 20;
        const y = -length / 2 + t * length;
        const x1 = Math.sin(t * Math.PI * 3 + mol.phase) * amplitude;
        const x2 = Math.sin(t * Math.PI * 3 + mol.phase + Math.PI) * amplitude;
        
        ctx.beginPath();
        ctx.moveTo(x1, y);
        ctx.lineTo(x2, y);
        ctx.stroke();
      }
      
      ctx.restore();
    };
    
    // Draw data stream (Matrix effect)
    const drawDataStream = (stream) => {
      ctx.font = '14px monospace';
      
      stream.chars.forEach((char, j) => {
        const y = stream.y - j * 18;
        if (y < 0 || y > canvas.height) return;
        
        const fade = 1 - j / stream.length;
        ctx.globalAlpha = stream.opacity * fade;
        
        if (j === 0) {
          ctx.fillStyle = '#d9f99d';
          ctx.shadowBlur = 10;
          ctx.shadowColor = '#a3e635';
        } else {
          ctx.fillStyle = '#a3e635';
          ctx.shadowBlur = 5;
          ctx.shadowColor = '#a3e635';
        }
        
        ctx.fillText(char, stream.x, y);
      });
      
      ctx.shadowBlur = 0;
    };
    
    // Draw energy pulse
    const drawEnergyPulse = (pulse) => {
      ctx.beginPath();
      ctx.arc(pulse.x, pulse.y, pulse.radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(163, 230, 53, ${pulse.opacity})`;
      ctx.lineWidth = 2;
      ctx.stroke();
    };
    
    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.016;
      
      // Update and draw data streams
      dataStreams.forEach(stream => {
        stream.y += stream.speed;
        if (stream.y - stream.length * 18 > canvas.height) {
          stream.y = -50;
          stream.x = Math.random() * canvas.width;
        }
        if (Math.random() < 0.02) {
          const idx = Math.floor(Math.random() * stream.chars.length);
          const chars = 'KETO01βγδ∆ATP'.split('');
          stream.chars[idx] = chars[Math.floor(Math.random() * chars.length)];
        }
        drawDataStream(stream);
      });
      
      // Update and draw molecules
      molecules.forEach(mol => {
        mol.x += mol.speedX;
        mol.y += mol.speedY;
        mol.rotation += mol.rotationSpeed;
        mol.phase += 0.02;
        
        // Breathing effect
        mol.opacity = (Math.sin(time + mol.phase) * 0.15 + 0.35);
        
        // Wrap around
        if (mol.x < -50) mol.x = canvas.width + 50;
        if (mol.x > canvas.width + 50) mol.x = -50;
        if (mol.y < -50) mol.y = canvas.height + 50;
        if (mol.y > canvas.height + 50) mol.y = -50;
        
        if (mol.type === 'ketone') drawKetone(mol);
        else drawDNA(mol);
      });
      
      // Update and draw energy pulses
      energyPulses = energyPulses.filter(pulse => {
        pulse.radius += pulse.speed;
        pulse.opacity -= 0.003;
        if (pulse.opacity > 0) {
          drawEnergyPulse(pulse);
          return true;
        }
        return false;
      });
      
      // Create new energy pulse occasionally
      if (Math.random() < 0.005 && energyPulses.length < 3) {
        energyPulses.push(createEnergyPulse());
      }
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.6 }}
    />
  );
});

export default KetoneMolecules;
